<script lang="ts">
  // The scalar facts about an activity -- the ones that are a single number rather than an average
  // with a spread. Anything with an avg/max pair lives in ActivityGaugeStats instead, and the
  // terrain numbers (elevation and KGE) on ActivityElevationStrip -- along with distance, unless
  // there is no terrain to draw, in which case distance comes back here as an ordinary metric.
  //
  // Three arrangements, per CardLayout:
  //   lg    -- an inline run; at that width a grid would be three rows of mostly whitespace
  //   md/sm -- label-above-value tiles on a fixed grid
  //   xs    -- a two-column table, label left and value right, one per row
  import DifficultyDiamonds from '@/DifficultyDiamonds.svelte';
  import { Activity } from '@/Common/ActivityFunctions';
  import { format_duration } from '@/time';
  import { kmToMile, useImperialUnits } from '@/Util/utils';
  import { type CardLayout, type DashboardActivity } from './activityCardUtils';

  export let exercise: DashboardActivity;
  export let layout: CardLayout = 'lg';
  /** Set by the card when ActivityElevationStrip isn't showing, since that's where distance lives. */
  export let showDistance: boolean = false;

  const imperial = useImperialUnits();

  $: duration = exercise?.duration ?? exercise?.adu;
  $: durationStr = duration ? format_duration(duration) : null;
  $: difficulty = exercise?.difficulty ?? null;
  /** "Tough - 128", or just the number for an activity the server didn't name a band for. */
  $: difficultyLabel = difficulty
    ? [exercise?.rating, Math.round(difficulty)].filter((part) => part != null && part !== '').join(' - ')
    : '';
  /**
   * Total work as energy burned. Xert's own figure everywhere it publishes one -- see the activity
   * payload in OAuthController, which overwrites whatever the device reported with this -- rather
   * than the fat/carb grams above, which are a different estimate and wouldn't agree with it.
   */
  $: calories = exercise?.tw ? Math.round((exercise.tw / 1000) * 1.1) : null;
  $: specRating = Activity.getSpecRating(exercise);
  $: focusLabel = [specRating, exercise?.focus].filter(Boolean).join(' ');

  /** `diamonds` renders the difficulty rating alongside its value; everything else is plain text. */
  type Metric = { label: string; value: string; title?: string; diamonds?: number };
  let metrics: Metric[] = [];
  $: metrics = ([
    showDistance && exercise?.distance
      ? {
          label: 'Distance',
          value: `${Math.round((imperial ? kmToMile(exercise.distance) : exercise.distance) * 10) / 10} ${imperial ? 'mi' : 'km'}`,
        }
      : null,
    // Name and number both, alongside the diamonds -- same three-part reading as the training
    // values panel ("◆◆◆◆ - Tough - 128"), where the diamonds give the band at a glance and the
    // number is what actually separates two rides that both round to four.
    difficulty
      ? { label: 'Difficulty', value: difficultyLabel, diamonds: difficulty }
      : null,
    durationStr ? { label: 'Duration', value: durationStr } : null,
    exercise?.xep ? { label: 'XEP', value: `${Math.round(exercise.xep)} W`, title: 'Xert Equivalent Power' } : null,
    focusLabel ? { label: 'Focus', value: focusLabel } : null,
    exercise?.tgf && exercise?.tgc
      ? { label: 'Fat / Carbs', value: `${Math.round(exercise.tgf)} g / ${Math.round(exercise.tgc)} g` }
      : null,
    calories ? { label: 'Calories', value: `${calories.toLocaleString()} kCal`, title: 'Energy burned' } : null,
  ] as (Metric | null)[]).filter((metric): metric is Metric => metric !== null);
</script>

{#if metrics.length}
  {#if layout === 'xs'}
    <div class="flex flex-col divide-y divide-x-0 divide-solid divide-border">
      {#each metrics as metric}
        <div class="flex flex-row items-center justify-between gap-3 py-1.5 first:pt-0 last:pb-0 min-w-0">
          <span class="text-[11px] uppercase tracking-wider text-muted-foreground shrink-0">{metric.label}</span>
          <span class="flex flex-row items-center gap-1.5 min-w-0" title={metric.title ?? metric.label}>
            {#if metric.diamonds}
              <DifficultyDiamonds difficulty={metric.diamonds} compact />
            {/if}
            <span class="text-sm font-semibold truncate">{metric.value}</span>
          </span>
        </div>
      {/each}
    </div>
  {:else if layout === 'lg'}
    <div class="flex flex-row flex-wrap items-baseline gap-x-5 gap-y-1.5 min-w-0">
      {#each metrics as metric}
        <div class="flex flex-row items-baseline gap-1.5 whitespace-nowrap min-w-0" title={metric.title ?? metric.label}>
          <span class="text-[10px] uppercase tracking-wider text-muted-foreground">{metric.label}</span>
          <span class="flex flex-row items-center gap-1.5">
            {#if metric.diamonds}
              <DifficultyDiamonds difficulty={metric.diamonds} compact />
            {/if}
            <span class="text-sm font-semibold truncate">{metric.value}</span>
          </span>
        </div>
      {/each}
    </div>
  {:else}
    <!-- Three across at both tiers: the md details column starts at ~460px, where a fourth column
         would leave no room for a value like "42 g / 478 g". -->
    <div class="grid grid-cols-3 gap-x-4 gap-y-2.5">
      {#each metrics as metric}
        <div class="min-w-0" title={metric.title ?? metric.label}>
          <div class="text-[10px] uppercase tracking-wider text-muted-foreground leading-none">{metric.label}</div>
          <div class="mt-1 flex flex-row items-center gap-1.5 min-w-0">
            {#if metric.diamonds}
              <DifficultyDiamonds difficulty={metric.diamonds} compact />
            {/if}
            <span class="text-sm font-semibold truncate">{metric.value}</span>
          </div>
        </div>
      {/each}
    </div>
  {/if}
{/if}
